import './Assistant.css';

import { Box, CircularProgress, MenuItem, Select } from '@mui/material';
import { CustomDrawer } from 'components/Common/CustomDrawer/Index';
import React, { useEffect, useState } from 'react';

import { ScribeEditCircleIcon } from '../../components/Icons/ScribeIcons';
import LibraryEditForm from './LibraryEditForm';

const languageOptions = [
  { value: '', display: 'All Languages' },
  { value: 'english', display: 'English' },
  { value: 'german', display: 'German' },
  { value: 'russian', display: 'Russian' },
  { value: 'italian', display: 'Italian' },
  { value: 'polish', display: 'Polish' },
  { value: 'ukrainian', display: 'Ukrainian' },
  { value: 'french', display: 'French' },
  { value: 'spanish', display: 'Spanish' },
];

const Library = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [globalData, setGlobalData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('');

  const oauthToken = localStorage.getItem('AUTH_TOKEN') || '';

  if (!oauthToken) {
    throw new Error('OAuth token is missing');
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const globalResponse = await fetch(
          `${baseURL}/api/instructions/global`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${oauthToken}`,
            },
          }
        );
        const globalDataResponse = await globalResponse.json();
        setGlobalData(globalDataResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleButtonClick = (item: any) => {
    setSelectedItem(item);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const updateGlobalData = (newGlobalData: any) => {
    setGlobalData(newGlobalData);
  };

  const specialtyOptions = Array.from(
    new Set(
      globalData
        .filter((item: any) =>
          selectedLanguage
            ? item.language.toLowerCase() === selectedLanguage
            : true
        )
        .map((item: any) => item.specialty || 'null')
    )
  ).sort();

  const formatOptions = Array.from(
    new Set(
      globalData
        .filter((item: any) =>
          selectedLanguage
            ? item.language.toLowerCase() === selectedLanguage
            : true
        )
        .map((item: any) => item.format || 'null')
    )
  ).sort();

  const filteredData = globalData.filter((item: any) => {
    const matchesLanguage = selectedLanguage
      ? item.language.toLowerCase() === selectedLanguage
      : true;

    let matchesSpecialty = true;
    if (selectedSpecialty) {
      if (item.specialty) {
        matchesSpecialty =
          item.specialty.toLowerCase() === selectedSpecialty.toLowerCase();
      } else {
        matchesSpecialty = selectedSpecialty === 'null';
      }
    }

    let matchesFormat = true;
    if (selectedFormat) {
      if (item.format) {
        matchesFormat =
          item.format.toLowerCase() === selectedFormat.toLowerCase();
      } else {
        matchesFormat = selectedFormat === 'null';
      }
    }

    return matchesLanguage && matchesSpecialty && matchesFormat;
  });

  useEffect(() => {
    if (selectedLanguage === '') {
      setSelectedSpecialty('');
      setSelectedFormat('');
    } else {
      setSelectedSpecialty('');
      setSelectedFormat('');
    }
  }, [selectedLanguage]);

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h3 className="assistant-heading">Assistant Market</h3>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '16px',
            marginTop: '8px',
          }}
        >
          {/* Language Dropdown */}
          <Box sx={{ maxWidth: 200 }}>
            <Select
              id="language"
              name="language"
              fullWidth
              displayEmpty
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              variant="outlined"
            >
              {languageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value === '' ? (
                    <em>{option.display}</em>
                  ) : (
                    option.display
                  )}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Specialty Dropdown */}
          <Box sx={{ maxWidth: 200 }}>
            <Select
              id="specialty"
              name="specialty"
              fullWidth
              displayEmpty
              value={
                specialtyOptions && specialtyOptions.length > 0
                  ? selectedSpecialty
                  : ''
              }
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              variant="outlined"
            >
              {specialtyOptions && specialtyOptions.length > 0 && (
                <MenuItem value="">
                  <em>All Specialties</em>
                </MenuItem>
              )}
              {specialtyOptions && specialtyOptions.length > 0 ? (
                specialtyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === 'null' ? (
                      <em>None</em>
                    ) : (
                      option.charAt(0).toUpperCase() + option.slice(1)
                    )}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">
                  <em>No specialties</em>
                </MenuItem>
              )}
            </Select>
          </Box>

          {/* Format Dropdown */}
          <Box sx={{ maxWidth: 200 }}>
            <Select
              id="format"
              name="format"
              fullWidth
              displayEmpty
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              variant="outlined"
            >
              <MenuItem value="">
                <em>All Formats</em>
              </MenuItem>

              {formatOptions.length > 0 ? (
                formatOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === 'null' ? (
                      <em>None</em>
                    ) : (
                      option.charAt(0).toUpperCase() + option.slice(1)
                    )}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">
                  <em>No format</em>
                </MenuItem>
              )}
            </Select>
          </Box>
        </div>
      </div>

      {isDrawerOpen && (
        <CustomDrawer
          heading="Edit Assistant"
          onOpenChange={handleCloseDrawer}
          width="50%"
        >
          <div className="pl-2">
            <LibraryEditForm
              closeForm={handleCloseDrawer}
              selectedItem={selectedItem}
              updateGlobalData={updateGlobalData}
            />
          </div>
        </CustomDrawer>
      )}

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
          }}
        >
          <CircularProgress sx={{ color: '#90bee6' }} />
        </div>
      ) : (
        (() => {
          if (filteredData.length === 0) {
            return (
              <div
                style={{
                  marginTop: '20px',
                  textAlign: 'center',
                  color: '#999',
                }}
              >
                No assistants found for the selected language, specialty, and
                format.
              </div>
            );
          }

          return filteredData.map((item: any) => (
            <div key={item.id} className="New-section">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex' }}>
                  <h3 className="New-section-text">{item.name}</h3>
                  <h6
                    className="assistant-text"
                    style={{
                      color:
                        item.category === 'letter' || item.category === 'Custom'
                          ? '#C11574'
                          : '#175CD3',
                      paddingRight:
                        item.category === 'procedure' ? '19px' : '8px',
                    }}
                  >
                    {item.category ? (
                      item.category.charAt(0).toUpperCase() +
                      item.category.slice(1)
                    ) : (
                      <em>None</em>
                    )}
                  </h6>
                  <h6 className="assistant-lan">
                    {item.language ? (
                      item.language.charAt(0).toUpperCase() +
                      item.language.slice(1)
                    ) : (
                      <em>None</em>
                    )}
                  </h6>
                  <h6
                    className="assistant-text"
                    style={{
                      color: item.format === 'Plain' ? '#C11574' : '#175CD3',
                    }}
                  >
                    {item.format ? item.format : <em>None</em>}
                  </h6>
                  <h6
                    className="assistant-specialty"
                    style={{
                      width: item.specialty === 'All' ? '30px' : '75px',
                    }}
                  >
                    {item.specialty ? item.specialty : <em>None</em>}
                  </h6>
                </div>
                <div style={{ marginRight: '3.1%', display: 'flex' }}>
                  <button
                    style={{
                      border: 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onClick={() => handleButtonClick(item)}
                  >
                    <div style={{ marginTop: '7px', marginRight: '8px' }}>
                      <ScribeEditCircleIcon />
                    </div>
                    <span className="view-new-button text-[#11AC9E]">View</span>
                  </button>
                </div>
              </div>
            </div>
          ));
        })()
      )}
    </div>
  );
};

export default Library;
